/**
 *
 * @module trilithiumPlayerAdapter
 * @desc PlayerAdapter for Sony Trilithium
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/media.md
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/stream-sample.md
 * @see https://github.bamtech.co/fed-core/browser-sdk/blob/master/docs/reference/PlayerEvents.md
 * @see https://github.bamtech.co/fed-core/browser-sdk/blob/master/docs/reference/PlayerProperties.md
 * @see https://github.bamtech.co/fed-ce/core-trilithium-video-harness
 *
 */

import Check from 'check-types';
import Services from './../../services/entry';
import Drm from './../../drm/entry';
import PlayerAdapter from './../playerAdapter';
import PlaybackMetrics from './../playbackMetrics';
import PlaybackEventListener from './../playbackEventListener';

const Util = Services.Util;
const DrmProvider = Drm.DrmProvider;
const DrmType = Services.Media.DrmType;

const ErrorReason = Services.Exception.ErrorReason;
const ExceptionReference = Services.Exception.ExceptionReference;
const ServiceException = Services.Exception.ServiceException;
const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    array,
    instanceStrict,
    nonEmptyString
} = Check.assert;

/**
 *
 * @class TrilithiumPlayerAdapter
 * @extends {PlayerAdapter}
 * @desc Interface used to communicate with the media player.
 *
 */
export default class TrilithiumPlayerAdapter extends PlayerAdapter {

    /**
     *
     * @param {NativePlayer} nativePlayer
     * @throws {InvalidArgumentException}
     *
     */
    constructor(nativePlayer) {

        const errorMessage = {
            ctor: 'TrilithiumPlayerAdapter(nativePlayer)',
            get nativePlayer() {
                return `${this.ctor} needs NativePlayer instance`;
            }
        };

        Check.assert.function(Util.getSafe(() => nativePlayer.play), errorMessage.nativePlayer, InvalidArgumentException); // eslint-disable-line

        super(nativePlayer);

        /**
         *
         * @access private
         * @type {Function|Null}
         * @since 2.0.1
         *
         */
        this.onError = nativePlayer.onError || null;

        /**
         *
         * @access private
         * @type {Function|Null}
         * @since 2.0.1
         *
         */
        this.onPlaying = nativePlayer.onPlaying || null;

        /**
         *
         * @access private
         * @type {Function|Null}
         * @since 2.0.1
         *
         */
        this.onEnded = nativePlayer.onEnded || null;

        /**
         *
         * @access private
         * @type {Function|Null}
         * @since 2.0.1
         *
         */
        this.onEncryptionKey = nativePlayer.onEncryptionKey || null;

    }

    /**
     *
     * @access public
     * @param {String} playlistUri - The URL to be used during playback.
     * @desc Callback used when prepare has been called (usually via the {PlaybackSession}).
     * Sets the source URI on the {NativePlayer} instance.
     * @returns {Promise<Void>}
     *
     */
    setSource(playlistUri) {

        const errorMessage = {
            method: 'TrilithiumPlayerAdapter.setSource(playlistUri)',
            get playlistUri() {
                return `${this.method} playlistUri should be a String with length`;
            }
        };

        nonEmptyString(playlistUri, errorMessage.playlistUri, InvalidArgumentException);

        return new Promise((resolve) => {

            this.playlistUri = playlistUri;

            return resolve();

        });

    }

    /**
     *
     * @access public
     * @since 2.0.0
     * @desc Gets a snapshot of information about media playback.
     * @throws {InvalidStateException} Unable to get NativePlayer playhead or bitrate data
     * @returns {Promise<PlaybackMetrics>} - instance that contains a snapshot
     * of information about media playback.
     * @note nativePlayer.currentBitrate - Applies only to live streaming.
     * @note nativePlayer.currentTime - The current cumulative time.
     * @note metric value is rounded down to prevent possible service issues with floats
     * @note executed by {PlaybackTelemetryDispatcher#recordStreamSample}
     *
     */
    getPlaybackMetrics() {

        const errorMessage = {
            method: 'TrilithiumPlayerAdapter.getPlaybackMetrics()',
            get currentPlayhead() {
                return `${this.method} unable to get NativePlayer playhead data`;
            },
            get currentBitrate() {
                return `${this.method} unable to get NativePlayer bitrate data`;
            }
        };

        const { nativePlayer, unavailableMetric } = this;

        if (Check.not.assigned(nativePlayer.currentBitrate)) {

            const reasons = [new ErrorReason('', errorMessage.currentBitrate)];
            const exceptionData = ExceptionReference.common.invalidState;

            return Promise.reject(new ServiceException({ reasons, exceptionData }));

        }

        if (Check.not.assigned(nativePlayer.currentTime)) {

            const reasons = [new ErrorReason('', errorMessage.currentPlayhead)];
            const exceptionData = ExceptionReference.common.invalidState;

            return Promise.reject(new ServiceException({ reasons, exceptionData }));

        }

        const currentBitrate = Math.floor(nativePlayer.currentBitrate) || unavailableMetric;
        const currentPlayhead = Math.floor(nativePlayer.currentTime) || unavailableMetric;

        return Promise.resolve(new PlaybackMetrics(currentBitrate, currentPlayhead));

    }

    /**
     *
     * @access public
     * @since 2.0.0
     * @param {PlaybackEventListener} listener
     * @throws {InvalidStateException} Unable to add PlaybackEventListener
     * @returns {Promise<Void>}
     *
     */
    addListener(listener) {

        const errorMessage = {
            method: 'TrilithiumPlayerAdapter.addListener(listener)',
            get listener() {
                return `${this.method} needs PlaybackEventListener instance`;
            },
            get addListener() {
                return `${this.method} unable to add PlaybackEventListener`;
            }
        };

        instanceStrict(listener, PlaybackEventListener, errorMessage.listener, InvalidArgumentException);

        const { boundHandlers, nativePlayer } = this;
        const { onPlay, onError, onPlayedToCompletion } = this;

        return new Promise((resolve, reject) => {

            if (Check.function(nativePlayer.play)) {

                this.listener = listener;

                boundHandlers.onPlay = onPlay.bind(this);
                boundHandlers.onError = onError.bind(this);
                boundHandlers.onPlayedToCompletion = onPlayedToCompletion.bind(this);

                this.setCallback('onPlaying', boundHandlers.onPlay);
                this.setCallback('onError', boundHandlers.onError);
                this.setCallback('onEnded', boundHandlers.onPlayedToCompletion);

                return resolve();

            }

            const reasons = [new ErrorReason('', errorMessage.addListener)];
            const exceptionData = ExceptionReference.common.invalidState;

            return reject(new ServiceException({ reasons, exceptionData }));

        });

    }

    /**
     *
     * @access public
     * @since 2.0.0
     * @param {PlaybackEventListener} listener
     * @returns {Promise<Void>}
     *
     */
    removeListener(listener) {

        const errorMessage = {
            method: 'TrilithiumPlayerAdapter.removeListener(listener)',
            get listener() {
                return `${this.method} needs PlaybackEventListener instance`;
            }
        };

        instanceStrict(listener, PlaybackEventListener, errorMessage.listener, InvalidArgumentException);

        const { nativePlayer } = this;

        return new Promise((resolve) => {

            if (Check.function(nativePlayer.play) && Check.equal(this.listener, listener)) {

                this.listener = null;

                nativePlayer.onPlaying = null;
                nativePlayer.onError = null;
                nativePlayer.onEnded = null;

            }

            return resolve();

        });

    }

    /**
     *
     * @access public
     * @since 3.2.0
     * @param {SDK.Drm.DrmProvider} drmProvider
     * @throws {InvalidStateException} This PlayerAdapter needs multiple SDK.Drm.DrmProvider instances,
     * please use TrilithiumPlayerAdapter#setDrmProviders(drmProviders) which takes in an
     * Array of SDK.Drm.DrmProvider instances
     *
     */
    setDrmProvider(drmProvider) { // eslint-disable-line

        const errorMessage = {
            method: 'TrilithiumPlayerAdapter.setDrmProvider(drmProvider)'
        };

        const reasons = [new ErrorReason('', `
            ${errorMessage.method} - this PlayerAdapter needs multiple SDK.Drm.DrmProvider instances,
            please use TrilithiumPlayerAdapter#setDrmProviders(drmProviders) which takes in an
            Array of SDK.Drm.DrmProvider instances
        `)];

        const exceptionData = ExceptionReference.common.invalidState;

        return Promise.reject(new ServiceException({ reasons, exceptionData }));

    }

    /**
     *
     * @access public
     * @since 3.3.0
     * @param {Array<SDK.Drm.DrmProvider>} drmProviders
     * @note filters out unsupported drm keys before creating the drm configuration object
     * @returns {Promise<Void>}
     *
     */
    setDrmProviders(drmProviders) {

        const errorMessage = {
            method: 'TrilithiumPlayerAdapter.setDrmProviders(drmProviders)',
            get drmProviders() {
                return `${this.method} needs Array of SDK.Drm.DrmProvider`;
            }
        };

        array.of.instanceStrict(drmProviders, DrmProvider, errorMessage.drmProviders, InvalidArgumentException);

        this.drmProviders = drmProviders;

        return this.attach();

    }

    /**
     *
     * @access public
     * @desc Resets player adapter state. Removes all playback listeners.
     * @returns {Promise<Void>}
     *
     */
    clean() {

        const { listener } = this;

        this.playlistUri = '';
        this.boundHandlers = {};
        this.onEncryptionKey = null;
        this.drmProvider = null;

        if (Check.assigned(listener)) {
            return this.removeListener(listener);
        }

        return Promise.resolve();

    }

    /**
     *
     * @access public
     * @since 3.1.0
     * @desc Completes the cleanup process by completely cleaning up all `PlayerAdapter`
     * references. This method should be executed by the application developer
     * when they no longer need to use a `PlayerAdapter` instance.
     * @note Because `clean` is automatically called by the `PlaybackSession` instance,
     * another method is necessary to handle the use case of an application developer who
     * wants to continue using a `PlayerAdapter` instance after a `PlaybackSession` has been released.
     * @returns {Promise<Void>}
     *
     */
    dispose() {

        this.nativePlayer = null;
        this.accessToken = null;
        this.onEncryptionKey = null;
        this.drmProvider = null;

        return Promise.resolve();

    }

    /* #region private: */

    /**
     *
     * @access private
     * @since 2.0.0
     * @returns {Promise<Void>}
     *
     */
    attach() {

        const callback = (keyObject) => {

            if (Check.assigned(keyObject.url) && Check.assigned(keyObject.method)) {

                this.silkCallback(
                    this.getDrmProviderByType(DrmType.SILK),
                    keyObject
                );

            } else {

                this.playReadyCallback(
                    this.getDrmProviderByType(DrmType.PLAYREADY),
                    keyObject
                );

            }

        };

        this.setCallback('onEncryptionKey', callback.bind(this));

        return Promise.resolve();

    }

    /**
     *
     * @access private
     * @param {String} key
     * @param {Function} eventCallback
     * @since 2.0.1
     *
     */
    setCallback(key, eventCallback) {

        const nativePlayer = this.nativePlayer;
        const clientCallback = this[key];

        if (Check.function(clientCallback)) {

            nativePlayer[key] = (value) => {

                clientCallback(value);
                eventCallback(value);

            };

        } else {

            nativePlayer[key] = eventCallback;

        }

    }

    /**
     *
     * @access private
     * @since 3.2.0
     * @param {SDK.Drm.SilkDrmProvider} drmProvider
     * @param {Object} keyObject
     * @throws {InvalidStateException} Unable to set encryption key
     * @returns {Promise<Void>}
     *
     */
    silkCallback(drmProvider, keyObject) {

        return new Promise((resolve, reject) => {

            drmProvider.getSilkKey(keyObject.url).then((key) => {

                keyObject.setEncryptionKey(key);
                return resolve();

            }).catch(() => {

                const reasons = [new ErrorReason('', 'Unable to set encryption key')];
                const exceptionData = ExceptionReference.common.invalidState;

                return reject(new ServiceException({ reasons, exceptionData }));

            });

        });

    }

    /**
     *
     * @access private
     * @since 3.2.0
     * @param {SDK.Drm.FairPlayDrmProvider} drmProvider
     * @param {Object} keyObject
     * @throws {InvalidStateException} Unable to obtain PlayReady license
     * @returns {Promise<Void>}
     *
     */
    playReadyCallback(drmProvider, keyObject) {

        const { nativePlayer } = this;

        return new Promise((resolve, reject) => {

            const onLicenseRequestReady = (format, body) => {

                drmProvider.getPlayReadyLicense({ xmlData: body.toString() }).then((data) => {

                    nativePlayer.setLicense(format, data);
                    return resolve();

                }).catch((ex) => {

                    const reasons = [new ErrorReason('', `Unable to obtain PlayReady license - "${ex.message}"`)];
                    const exceptionData = ExceptionReference.common.invalidState;

                    return reject(new ServiceException({ reasons, exceptionData }));

                });

            };

            nativePlayer.onLicenseRequestReady = onLicenseRequestReady.bind(this);

            for (const key of keyObject.keyTable) {

                nativePlayer.generateLicenseRequest(
                    Util.getSafe(() => key.format),
                    Util.getSafe(() => key.url)
                );

            }

        });

    }

    /**
     *
     * @access private
     * @param {SDK.Services.Media.DrmType} drmType
     * @since 3.3.0
     * @returns {SDK.Drm.DrmProvider}
     *
     */
    getDrmProviderByType(drmType) {

        const { drmProviders } = this;
        let drmProvider;

        drmProviders.find((provider) => {

            if (provider.type === drmType) {
                drmProvider = provider;
                return true;
            }

            return false;

        });

        return drmProvider;

    }

}



// WEBPACK FOOTER //
// ./src/media/playerAdapter/trilithiumPlayerAdapter.js